import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Hidden from '@mui/material/Hidden';
import List from '@mui/material/List';
import React, { useCallback, useEffect, useState } from 'react';

import AsyncLoader from '~components/AsyncLoader';
import EmptyState from '~components/EmptyState';
import GenericDataCard from '~components/GenericDataCard';
import { createTrunkFlow, deleteTrunkFlow, getTrunkFlows, updateTrunkFlow } from '~pages/SipConfiguration/api';
import { TrunkFlow, UpsertTrunkFlow } from '~pages/SipConfiguration/domain';
import { useNotification } from '~providers/NotificationProvider';
import { APIError, UnsupportedStructureError } from '~services/Errors';

import TrunkFlowModal from './TrunkFlowModal';

type Error = {
  text: string;
  subText: string;
};

const DefaultTrunkFlow = '*';

const TrunkFlows = () => {
  const { pushNotification } = useNotification();
  const [pageLoaded, setPageLoaded] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [editingTrunkFlow, setEditingTrunkFlow] = useState<TrunkFlow | null>(null);
  const [submittingData, setSubmittingData] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [trunkFlows, setTrunkFlows] = useState<TrunkFlow[]>([]);

  const load = async () => {
    try {
      setTrunkFlows(await getTrunkFlows());
    } catch (e) {
      handleError(e);
    } finally {
      setPageLoaded(true);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleError = (e: any) => {
    if (e instanceof APIError) {
      setError({ text: 'Unable to request data from backend', subText: e.message });
    }

    if (e instanceof UnsupportedStructureError) {
      setError({ text: 'Data from backend Invalid', subText: 'Unable to decode response' });
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingTrunkFlow(null);
  };

  const upsertTrunkFlow = useCallback(
    async (data: UpsertTrunkFlow) => {
      setSubmittingData(true);

      try {
        let msg = '';
        if (editingTrunkFlow === null) {
          await createTrunkFlow(data);
          msg = `Created trunk flow ${data.trunk}`;
        } else {
          await updateTrunkFlow(data);
          msg = `Updated trunk flow ${data.trunk}`;
        }
        pushNotification('success', msg);
        load();
        closeModal();
      } catch (e) {
        pushNotification('error', (e as APIError | UnsupportedStructureError).message);
        return;
      } finally {
        setSubmittingData(false);
      }
    },
    [editingTrunkFlow],
  );

  const deleteTrunkFlowFn = async (trunk: string) => {
    try {
      await deleteTrunkFlow(trunk);
      load();
      pushNotification('success', `Deleted trunk flow ${trunk}`);
    } catch (e) {
      pushNotification('error', (e as APIError | UnsupportedStructureError).message);
    }
  };

  const noResults = trunkFlows.length === 0;
  const errorDisplay = error ? <EmptyState type='error' text={error.text} subText={error.subText} /> : null;

  return (
    <AsyncLoader isLoading={!pageLoaded} error={errorDisplay}>
      {noResults && (
        <EmptyState
          type='no-items-3'
          text='No Trunk Flows currently exist'
          subText='Create a Trunk Flow by clicking the button below'
          action={() => setModalOpen(true)}
          actionText='Create Trunk Flow'
        />
      )}

      {!noResults && (
        <Grid container spacing={1} alignContent='center'>
          <Hidden smDown>
            <Grid item md={9}></Grid>
          </Hidden>

          <Grid item style={{ display: 'flex', alignItems: 'center' }} xs={12} md={3}>
            <Button
              variant='contained'
              color='primary'
              disableElevation
              fullWidth
              startIcon={<AddIcon />}
              onClick={() => setModalOpen(true)}>
              Create Trunk Flow
            </Button>
          </Grid>

          <Grid item xs={12}>
            <List>
              {trunkFlows.map((item) => (
                <GenericDataCard
                  key={item.trunk}
                  title={item.trunk}
                  subTitle={`Contact Flow: ${item.contactFlowName}`}
                  deleteMessage={`You are about to delete trunk flow ${item.trunk}.`}
                  onEdit={() => setEditingTrunkFlow(item)}
                  onDelete={item.trunk !== DefaultTrunkFlow ? () => deleteTrunkFlowFn(item.trunk) : undefined}
                />
              ))}
            </List>
          </Grid>
        </Grid>
      )}

      <TrunkFlowModal
        open={modalOpen || Boolean(editingTrunkFlow)}
        submitting={submittingData}
        trunk={editingTrunkFlow}
        onClose={closeModal}
        onAccept={upsertTrunkFlow}
      />
    </AsyncLoader>
  );
};

export default TrunkFlows;
